// Realtime notifications WS (per-user channel)
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { tokens, refreshAccessToken } from "../lib/api";

// BUG-003 : le jeton voyage par le sous-protocole WebSocket, jamais en
// query string — uvicorn journalise la ligne de requete complete, et un
// jeton vaut trente minutes de controle du compte.
const SOUS_PROTOCOLE_WS = "knowrok.bearer";

export default function useUserNotificationsRealtime(enabled = true) {
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    stoppedRef.current = false;

    const base = process.env.REACT_APP_BACKEND_URL || "";

    const connect = () => {
      if (stoppedRef.current) return;
      const access = tokens.access;
      if (!access) return;
      const wsUrl =
        base.replace(/^http/, "ws") +
        `/api/ws/user`;
      const ws = new WebSocket(wsUrl, [SOUS_PROTOCOLE_WS, access]);
      wsRef.current = ws;
      let opened = false;

      ws.onopen = () => {
        opened = true;
        backoffRef.current = 1000;
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "notification.created") {
            qc.invalidateQueries({ queryKey: ["notifications"] });
            qc.invalidateQueries({ queryKey: ["unread-count"] });
            qc.invalidateQueries({ queryKey: ["dashboard"] });
            const n = msg.data || {};
            if (n.type === "memo.urgent") {
              toast.error(`🔴 ${n.title || "Mémo urgent"}`, {
                description: n.body,
                duration: 10000,
              });
            } else {
              toast(n.title || "Nouvelle notification", {
                description: n.body,
                duration: 4000,
              });
            }
          } else if (msg.type === "notification.read") {
            qc.invalidateQueries({ queryKey: ["notifications"] });
            qc.invalidateQueries({ queryKey: ["unread-count"] });
          }
        } catch (_e) {
          /* noop */
        }
      };

      ws.onclose = () => {
        if (stoppedRef.current) return;
        const delay = Math.min(backoffRef.current, 8000);
        backoffRef.current = Math.min(backoffRef.current * 2, 8000);
        // Socket jamais ouverte = handshake refusé (token expiré) :
        // on rafraîchit avant de retenter, et on arrête si le refresh échoue.
        if (!opened) {
          refreshAccessToken()
            .then(() => setTimeout(connect, delay))
            .catch(() => {
              stoppedRef.current = true;
            });
          return;
        }
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch (_e) {
          /* noop */
        }
      };
    };

    connect();
    return () => {
      stoppedRef.current = true;
      try {
        wsRef.current?.close();
      } catch (_e) {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
